import React from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, Area, AreaChart, Legend } from "recharts";
import {
  Users, AlertTriangle, MessageSquareWarning, BadgeIndianRupee, KeyRound, Building2,
  TrendingUp, TrendingDown, Activity, Flame,
} from "lucide-react";
import { api, fmtINR, fmtINRcompact, relativeTime } from "@/lib/api";
import { Skeleton, PriorityDot, StatusPill } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);
const PRIO_COLORS = { P1: "#FF3B30", P2: "#FF9500", P3: "#F9A61C", P4: "#8E8E93" };

const HeroStat = ({ Icon, label, value, valueMobile, delta, accent, alert, tid }) => (
  <div className="rc-card rc-rise" data-testid={tid} style={{ padding: "16px" }}>
    <div className="flex items-start justify-between">
      <div className={`rc-icon-tile ${accent || "rc-icon-tile-blue"}`} style={{ width: 40, height: 40, borderRadius: 10 }}>
        <Icon size={18} strokeWidth={1.75}/>
      </div>
      {alert && <span className="rc-pulse-red rc-dot rc-dot-p1 mt-1"/>}
    </div>
    <div className="mt-2 rc-caption uppercase tracking-wider" style={{ fontSize: 11 }}>{label}</div>
    <div className="rc-title-2 rc-tabular mt-0.5 sm:hidden" style={{ fontSize: 20, lineHeight: 1.1 }}>{valueMobile ?? value}</div>
    <div className="rc-title-2 rc-tabular mt-0.5 hidden sm:block" style={{ fontSize: 24, lineHeight: 1.1 }}>{value}</div>
    {delta != null && (
      <div className={`rc-caption mt-1 flex items-center gap-1 ${delta >= 0 ? "text-[var(--rc-success)]" : "text-[var(--rc-destructive)]"}`}>
        {delta >= 0 ? <TrendingUp size={12}/> : <TrendingDown size={12}/>} {Math.abs(delta)}% vs last week
      </div>
    )}
  </div>
);

export default function MgmtDashboard() {
  const { data } = useSWR("/management/dashboard", fetcher, { refreshInterval: 10000 });

  if (!data) return <Skeleton h={400}/>;
  const k = data.kpis;
  const heat = data.heatmap;

  const cellClass = (n) => {
    if (n === 0) return "rc-cell rc-cell-0";
    if (n <= 3) return "rc-cell rc-cell-low";
    if (n <= 7) return "rc-cell rc-cell-med";
    return "rc-cell rc-cell-hi";
  };

  // crude deltas from trend
  const trend = data.ticket_trend || [];
  const totalOpened = trend.reduce((a, t) => a + t.opened, 0);
  const totalClosed = trend.reduce((a, t) => a + t.closed, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="rc-eyebrow">Command centre</div>
          <div className="rc-title-1 mt-1">Operations at a glance</div>
          <div className="rc-callout">Live overview across all projects, queries and collections.</div>
        </div>
        <div className="rc-card rc-card-padded flex items-center gap-3" style={{ padding: "12px 16px" }}>
          <div className="rc-icon-tile rc-icon-tile-green" style={{ width: 36, height: 36, borderRadius: 10 }}><Activity size={16}/></div>
          <div>
            <div className="rc-footnote">Live · auto-refresh 10s</div>
            <div className="rc-headline rc-tabular">{totalOpened} opened · {totalClosed} closed · 14d</div>
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <HeroStat tid="kpi-customers"   Icon={Users}              label="Active Customers" value={k.total_customers}/>
        <HeroStat tid="kpi-flats"        Icon={Building2}          label="Flats Booked"     value={`${k.booked}/${k.total_flats}`}/>
        <HeroStat tid="kpi-open"         Icon={MessageSquareWarning} label="Open Tickets"   value={k.open_tickets}/>
        <HeroStat tid="kpi-breached"     Icon={AlertTriangle}      label="SLA Breaches"     value={k.sla_breached} accent="rc-icon-tile-orange" alert={k.sla_breached > 0}/>
        <HeroStat tid="kpi-p1"           Icon={Flame}              label="P1 Open"          value={k.p1_open}  accent="rc-icon-tile-orange"/>
        <HeroStat tid="kpi-outstanding"  Icon={BadgeIndianRupee}   label="Outstanding"      value={fmtINR(k.outstanding)} valueMobile={fmtINRcompact(k.outstanding)} accent="rc-icon-tile-gold"/>
      </div>

      {/* Heatmap */}
      <div className="rc-card rc-card-padded rc-rise" style={{ animationDelay: "100ms" }}>
        <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="rc-title-3">Ticket heatmap</div>
            <div className="rc-footnote">Projects × Categories · tap any cell to drill in</div>
          </div>
          <div className="flex items-center gap-2 rc-footnote flex-wrap">
            <span className="flex items-center gap-1"><span className="rc-cell rc-cell-0 !w-4 !h-4 border border-[var(--rc-line)]"></span> 0</span>
            <span className="flex items-center gap-1"><span className="rc-cell rc-cell-low !w-4 !h-4"></span> 1–3</span>
            <span className="flex items-center gap-1"><span className="rc-cell rc-cell-med !w-4 !h-4"></span> 4–7</span>
            <span className="flex items-center gap-1"><span className="rc-cell rc-cell-hi !w-4 !h-4"></span> 8+</span>
          </div>
        </div>
        <div className="overflow-x-auto -mx-5 px-5 rc-heatmap-wrap">
          <table className="rc-heatmap">
            <thead>
              <tr>
                <th className="rc-heatmap-sticky"></th>
                {heat.categories.map(c => (
                  <th key={c} className="rc-heatmap-col-h">
                    {c.split(" ").slice(0, 2).join(" ")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {heat.rows.map(r => (
                <tr key={r.project_id}>
                  <td className="rc-heatmap-sticky rc-heatmap-row-h">{r.project_name}</td>
                  {r.cells.map((c, i) => (
                    <td key={i} className="p-1">
                      <Link to={`/management/escalations`} className={`${cellClass(c.count)} !w-14 !h-12 sm:!w-20 sm:!h-15`} data-testid={`heat-${r.project_id}-${i}`}>
                        {c.count}
                      </Link>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="rc-card rc-card-padded rc-rise" style={{ animationDelay: "160ms" }}>
          <div className="rc-title-3">Ticket trend</div>
          <div className="rc-footnote mb-3">Last 14 days — opened vs closed</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trend} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="rcOpened" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0F4B7F" stopOpacity={0.35}/>
                  <stop offset="100%" stopColor="#0F4B7F" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="rcClosed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34C759" stopOpacity={0.35}/>
                  <stop offset="100%" stopColor="#34C759" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="date" stroke="#8E8E93" fontSize={10} tickFormatter={(d) => d.slice(5)}/>
              <YAxis stroke="#8E8E93" fontSize={10} allowDecimals={false}/>
              <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}/>
              <Area type="monotone" dataKey="opened" stroke="#0F4B7F" strokeWidth={2} fill="url(#rcOpened)"/>
              <Area type="monotone" dataKey="closed" stroke="#34C759" strokeWidth={2} fill="url(#rcClosed)"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rc-card rc-card-padded rc-rise" style={{ animationDelay: "220ms" }}>
          <div className="rc-title-3">Priority mix</div>
          <div className="rc-footnote mb-3">All open tickets, by criticality</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.priority_breakdown} dataKey="count" nameKey="priority" innerRadius={48} outerRadius={82} paddingAngle={3}>
                {data.priority_breakdown.map((p, i) => <Cell key={i} fill={PRIO_COLORS[p.priority]}/>)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}/>
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }}/>
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="rc-card rc-card-padded rc-rise" style={{ animationDelay: "280ms" }}>
          <div className="rc-title-3">Top categories</div>
          <div className="rc-footnote mb-3">Where customers are reaching out</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={heat.categories.map((cat, i) => ({ cat: cat.split(" ")[0], count: heat.rows.reduce((a, r) => a + r.cells[i].count, 0) }))} margin={{ left: 0 }}>
              <XAxis dataKey="cat" stroke="#8E8E93" fontSize={10}/>
              <YAxis stroke="#8E8E93" fontSize={10} allowDecimals={false}/>
              <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}/>
              <Bar dataKey="count" fill="#F27024" radius={[8, 8, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top escalations */}
      <div className="rc-card rc-card-padded rc-rise" style={{ animationDelay: "340ms" }}>
        <div className="rc-title-3 mb-1 flex items-center gap-2">
          <span className="rc-icon-tile rc-icon-tile-orange" style={{ width: 32, height: 32, borderRadius: 10 }}>
            <AlertTriangle size={16}/>
          </span>
          Top escalations
        </div>
        <div className="rc-footnote mb-3">P1 critical &amp; SLA-breached, sorted by oldest deadline</div>
        <div className="divide-y divide-[var(--rc-line)]">
          {data.escalations.map(t => (
            <Link to={`/crm/tickets/${t.ticket_id}`} key={t.ticket_id} className="py-3 flex items-center gap-3 hover:bg-[var(--rc-near-white)] -mx-2 px-2 rounded-lg group">
              <PriorityDot priority={t.priority}/>
              <div className="flex-1 min-w-0">
                <div className="rc-headline truncate">{t.subject}</div>
                <div className="rc-caption">#{t.ticket_id.slice(0,8)} · {t.category}</div>
              </div>
              <StatusPill status={t.sla_breached ? "breached" : t.status}/>
              <div className="rc-footnote hidden sm:block">{relativeTime(t.created_at)}</div>
            </Link>
          ))}
          {data.escalations.length === 0 && <div className="rc-footnote py-6 text-center">No escalations — operations running smoothly.</div>}
        </div>
      </div>
    </div>
  );
}
